import { useEffect, useState } from "react";
import { API_CACHE_TTL, fetchCachedApi } from "@/utils/apiCache";

export interface TourEvent {
  id: string;
  title?: string;
  datetime: string;
  venue: {
    name: string;
    city: string;
    region: string;
    country: string;
    location?: string;
    latitude?: number;
    longitude?: number;
  };
  lineup: string[];
  offers: Array<{
    type?: string;
    url: string;
    status?: string;
  }>;
  url: string;
  description?: string;
}

export interface TourDatesResponse {
  artist: string;
  events: TourEvent[];
  count: number;
  message?: string;
}

interface UseTourDatesReturn {
  data: TourDatesResponse | null;
  loading: boolean;
  error: string | null;
}

function useTourDates(
  artistName: string,
  dependencies: React.DependencyList = []
): UseTourDatesReturn {
  const [data, setData] = useState<TourDatesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!artistName) return;

    const fetchTourDates = async () => {
      setLoading(true);
      setError(null);

      try {
        const json = await fetchCachedApi<TourDatesResponse>(
          "/.netlify/functions/GetTourDates?artistName=" + encodeURIComponent(artistName),
          {},
          {
            ttlMs: API_CACHE_TTL.SIX_HOURS,
            shouldCache: (data) => Array.isArray(data?.events),
          }
        );
        setData(json);
      } catch (err) {
        console.error('Error fetching tour dates:', err);
        setError((err as Error).message);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTourDates();
  }, [artistName, ...dependencies]);

  return { data, loading, error };
}

export default useTourDates;
